import { useEffect, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';
import ApperIcon from '@/components/ApperIcon';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

export const WHATSAPP_USERNAME = 'ghostcable';
export const WHATSAPP_URL = `https://wa.me/${WHATSAPP_USERNAME}`;

/** WhatsApp glyph (Simple Icons, CC0). Lucide has no brand icons. */
function WhatsAppGlyph({ size = 24, className }) {
  return (
    <svg viewBox="0 0 24 24" width={size} height={size} fill="currentColor" aria-hidden="true" className={className}>
      <path d="M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.870 9.870 0 0 1-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.860 9.860 0 0 1-1.510-5.260c.001-5.450 4.436-9.884 9.888-9.884 2.640 0 5.122 1.030 6.988 2.898a9.825 9.825 0 0 1 2.893 6.994c-.003 5.450-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0 0 12.050 0C5.495 0 .160 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 0 0 5.683 1.448h.005c6.554 0 11.890-5.335 11.893-11.893a11.821 11.821 0 0 0-3.480-8.413Z" />
    </svg>
  );
}

/**
 * Small "Contact now" bubble pinned to the bottom-right corner, for signed-out visitors.
 * Signed-in customers use the ticket system instead, so the layouts only render it when nobody is signed in.
 */
export default function WhatsAppContact({ className }) {
  const [open, setOpen] = useState(false);
  const boxRef = useRef(null);

  useEffect(() => {
    if (!open) return undefined;
    const onKey = (e) => e.key === 'Escape' && setOpen(false);
    const onDown = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('keydown', onKey);
    document.addEventListener('pointerdown', onDown);
    return () => {
      document.removeEventListener('keydown', onKey);
      document.removeEventListener('pointerdown', onDown);
    };
  }, [open]);

  async function copy() {
    try {
      await navigator.clipboard.writeText(`@${WHATSAPP_USERNAME}`);
      toast.success(`Copied @${WHATSAPP_USERNAME}`);
    } catch {
      toast.error('Could not copy. The username is @' + WHATSAPP_USERNAME);
    }
  }

  return (
    <div
      ref={boxRef}
      className={cn(
        'fixed right-4 z-(--z-sticky) flex flex-col items-end gap-3 print:hidden md:right-6',
        'bottom-[max(1rem,env(safe-area-inset-bottom))] md:bottom-6',
        className,
      )}
    >
      {open && (
        <div
          id="whatsapp-contact"
          role="dialog"
          aria-label="Contact us on WhatsApp"
          className="w-[min(18rem,calc(100vw-2rem))] rounded-lg border border-border bg-popover p-4 text-popover-foreground shadow-lg"
        >
          <div className="flex items-start justify-between gap-3">
            <div className="flex items-center gap-2.5">
              <span className="flex size-9 shrink-0 items-center justify-center rounded-full bg-success text-success-foreground">
                <WhatsAppGlyph size={20} />
              </span>
              <div className="min-w-0">
                <p className="text-sm font-semibold leading-tight">Chat on WhatsApp</p>
                <p className="font-mono text-xs text-muted-foreground">@{WHATSAPP_USERNAME}</p>
              </div>
            </div>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="-mr-2 -mt-2 size-8"
              aria-label="Close"
              onClick={() => setOpen(false)}
            >
              <ApperIcon name="X" size={16} />
            </Button>
          </div>
          <p className="mt-3 text-sm text-muted-foreground">
            Questions about a plan or setup before you buy? Message us and a real person answers.
          </p>
          <Button asChild className="mt-3 w-full bg-success text-success-foreground hover:bg-success/90">
            <a href={WHATSAPP_URL} target="_blank" rel="noopener noreferrer" onClick={() => setOpen(false)}>
              <WhatsAppGlyph size={16} />
              Open WhatsApp
            </a>
          </Button>
          <button
            type="button"
            onClick={copy}
            className="mt-2 flex w-full items-center justify-center gap-1.5 rounded-md py-1.5 text-xs text-muted-foreground transition-colors hover:text-foreground"
          >
            <ApperIcon name="Copy" size={14} />
            Or search @{WHATSAPP_USERNAME} in WhatsApp
          </button>
          <p className="mt-2 border-t border-border pt-2 text-center text-xs text-muted-foreground">
            Already a customer?{' '}
            <Link to="/customer/login" onClick={() => setOpen(false)} className="text-primary hover:underline">
              Sign in
            </Link>{' '}
            and open a ticket.
          </p>
        </div>
      )}

      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        aria-expanded={open}
        aria-controls="whatsapp-contact"
        aria-label={open ? 'Close WhatsApp contact' : 'Contact us on WhatsApp'}
        className={cn(
          'group flex h-12 items-center gap-2 rounded-full bg-success pl-3 pr-3 text-success-foreground shadow-lg ring-1 ring-success-border',
          'transition-transform duration-(--transition-fast) hover:-translate-y-0.5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring sm:pr-4',
        )}
      >
        {open ? <ApperIcon name="X" size={22} /> : <WhatsAppGlyph size={24} />}
        <span className="hidden text-sm font-semibold sm:inline">{open ? 'Close' : 'Contact now'}</span>
      </button>
    </div>
  );
}
